import React, { useCallback, useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  XCircle, CalendarClock, Plus, ImagePlus, X, Heart, MessageCircle, Send, Bookmark,
  MoreHorizontal, ChevronLeft, ChevronRight, Pencil, Upload, Eye,
} from 'lucide-react';
import * as api from '../../api/client';
import { AppError } from '../../api/request';
import { useInstagramAccounts } from '../../context/InstagramAccountContext';
import PageHeader from '../components/PageHeader';
import NoAccountState from '../components/NoAccountState';

const STATUS_STYLE: Record<string, { labelKey: string; className: string }> = {
  scheduled: { labelKey: 'schedule.status.scheduled', className: 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300' },
  publishing: { labelKey: 'schedule.status.publishing', className: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' },
  published: { labelKey: 'schedule.status.published', className: 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300' },
  failed: { labelKey: 'schedule.status.failed', className: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300' },
};

/** Upcoming / in-flight / failed — everything except published history. */
const SCHEDULED_BUCKET = new Set(['scheduled', 'publishing', 'failed']);

const ACCEPTED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_IMAGES = 10;
const PAGE_SIZE = 20;

type ImageItem = { id: string; file?: File; url: string; remote?: boolean };

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultScheduleLocal(): string {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return toLocalInputValue(d);
}

function parseHashtagTokens(raw: string): string[] {
  return raw
    .replace(/,/g, ' ')
    .split(/\s+/)
    .map((t) => t.replace(/^#/, '').trim())
    .filter(Boolean);
}

function formatPreviewCaption(caption: string, hashtagsRaw: string): string {
  const tags = parseHashtagTokens(hashtagsRaw);
  const tagLine = tags.map((t) => `#${t}`).join(' ');
  if (!caption.trim()) return tagLine;
  if (!tagLine) return caption.trim();
  return `${caption.trim()}\n\n${tagLine}`;
}

function newImageItem(file: File): ImageItem {
  return { id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`, file, url: URL.createObjectURL(file) };
}

function remoteImageItem(url: string, index: number): ImageItem {
  return { id: `remote-${index}-${url}`, url, remote: true };
}

function isFutureScheduled(p: api.ScheduledPost): boolean {
  return p.status === 'scheduled' && new Date(p.scheduledAt).getTime() > Date.now();
}

function CardHashtags({
  tags,
  moreLabel,
  onMore,
}: {
  tags: string[];
  moreLabel: (count: number) => string;
  onMore?: () => void;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLDivElement>(null);
  const moreMeasureRef = React.useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(tags.length);
  const tagsKey = tags.join('\0');

  useEffect(() => {
    const container = containerRef.current;
    if (!container || tags.length === 0) {
      setVisibleCount(tags.length);
      return;
    }

    function recalc() {
      const el = containerRef.current;
      const row = measureRef.current;
      if (!el || !row) return;

      const maxWidth = el.clientWidth;
      const chips = Array.from(row.children) as HTMLElement[];
      if (chips.length === 0) {
        setVisibleCount(0);
        return;
      }

      const gap = 6; // gap-1.5
      let used = 0;
      let fit = 0;

      for (let i = 0; i < chips.length; i++) {
        const chipW = chips[i].offsetWidth;
        const next = used + (fit > 0 ? gap : 0) + chipW;
        if (next <= maxWidth) {
          used = next;
          fit += 1;
        } else {
          break;
        }
      }

      if (fit >= chips.length) {
        setVisibleCount(chips.length);
        return;
      }

      // Reserve space for the "more" chip; drop tags until the rest + more fit.
      const moreW = moreMeasureRef.current?.offsetWidth ?? 56;
      while (fit > 0) {
        let width = moreW;
        for (let i = 0; i < fit; i++) {
          width += chips[i].offsetWidth + gap;
        }
        if (width <= maxWidth) break;
        fit -= 1;
      }

      setVisibleCount(Math.max(0, fit));
    }

    // Defer one frame so offscreen measure nodes have layout.
    const raf = requestAnimationFrame(recalc);
    const ro = new ResizeObserver(recalc);
    ro.observe(container);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [tags, tagsKey]);

  if (tags.length === 0) return null;

  const hidden = Math.max(0, tags.length - visibleCount);

  return (
    <div ref={containerRef} className="relative min-w-0">
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none absolute -left-[9999px] top-0 flex gap-1.5 whitespace-nowrap opacity-0"
      >
        {tags.map((tag) => (
          <span
            key={tag}
            className="shrink-0 rounded bg-white px-2 py-1 text-[11px] font-medium text-lc-purple dark:bg-lc-bg dark:text-lc-purple"
          >
            #{tag}
          </span>
        ))}
      </div>
      <span
        ref={moreMeasureRef}
        aria-hidden
        className="pointer-events-none absolute -left-[9999px] top-0 shrink-0 rounded bg-white px-2 py-1 text-[11px] font-medium text-gray-500 dark:bg-lc-bg dark:text-lc-faint"
      >
        {moreLabel(Math.max(1, tags.length))}
      </span>

      <div className="flex flex-nowrap items-center gap-1.5 overflow-hidden">
        {tags.slice(0, visibleCount).map((tag) => (
          <span
            key={tag}
            className="shrink-0 rounded bg-white px-2 py-1 text-[11px] font-medium text-lc-purple dark:bg-lc-bg dark:text-lc-purple"
          >
            #{tag}
          </span>
        ))}
        {hidden > 0 && (
          onMore ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMore();
              }}
              className="shrink-0 rounded bg-white px-2 py-1 text-[11px] font-medium text-gray-500 hover:text-lc-purple dark:bg-lc-bg dark:text-lc-faint dark:hover:text-lc-purple"
            >
              {moreLabel(hidden)}
            </button>
          ) : (
            <span className="shrink-0 rounded bg-white px-2 py-1 text-[11px] font-medium text-gray-500 dark:bg-lc-bg dark:text-lc-faint">
              {moreLabel(hidden)}
            </span>
          )
        )}
      </div>
    </div>
  );
}

function CardMediaPreview({
  urls,
  carouselLabel,
  prevLabel,
  nextLabel,
}: {
  urls: string[];
  carouselLabel: string;
  prevLabel: string;
  nextLabel: string;
}) {
  const [index, setIndex] = useState(0);
  const touchStartX = React.useRef<number | null>(null);

  if (urls.length === 0) {
    return (
      <div className="flex aspect-square w-full items-center justify-center rounded-md border border-[#e8ddd0] bg-white/50 text-gray-300 dark:border-lc-borderStrong dark:bg-lc-bg dark:text-lc-faint">
        <CalendarClock className="h-8 w-8" />
      </div>
    );
  }

  const multi = urls.length > 1;
  const safeIndex = Math.min(index, urls.length - 1);

  function goPrev(e?: React.MouseEvent) {
    e?.stopPropagation();
    setIndex((i) => (i <= 0 ? urls.length - 1 : i - 1));
  }
  function goNext(e?: React.MouseEvent) {
    e?.stopPropagation();
    setIndex((i) => (i >= urls.length - 1 ? 0 : i + 1));
  }

  return (
    <div
      className="relative aspect-square w-full overflow-hidden rounded-md border border-[#e8ddd0] bg-gray-100 dark:border-lc-borderStrong dark:bg-lc-card touch-pan-y"
      onTouchStart={(e) => {
        touchStartX.current = e.changedTouches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current == null || !multi) return;
        const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(dx) < 40) return;
        if (dx < 0) goNext();
        else goPrev();
      }}
    >
      <img src={urls[safeIndex]} alt="" className="absolute inset-0 h-full w-full object-cover" />
      {multi && (
        <>
          <span className="absolute top-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-semibold text-white">
            {carouselLabel}
          </span>
          <span className="absolute top-2 left-2 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white">
            {safeIndex + 1}/{urls.length}
          </span>
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-white/90 text-gray-800 shadow flex items-center justify-center hover:opacity-100 opacity-90"
            aria-label={prevLabel}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={goNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-white/90 text-gray-800 shadow flex items-center justify-center hover:opacity-100 opacity-90"
            aria-label={nextLabel}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <div className="absolute bottom-2.5 inset-x-0 flex justify-center gap-1.5">
            {urls.map((url, i) => (
              <button
                key={url}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex(i);
                }}
                className={`h-1.5 rounded-full transition-all ${
                  i === safeIndex ? 'w-3 bg-white' : 'w-1.5 bg-white/50'
                }`}
                aria-label={`${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function SchedulePage() {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const titleId = useId();
  const descId = useId();
  const { activeAccount } = useInstagramAccounts();
  const [posts, setPosts] = useState<api.ScheduledPost[]>([]);
  const [page, setPage] = useState(0);
  const [showScheduled, setShowScheduled] = useState(true);
  const [showPublished, setShowPublished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<api.ScheduledPost | null>(null);
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [scheduledAt, setScheduledAt] = useState(defaultScheduleLocal);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const suggestReqId = React.useRef(0);

  const load = useCallback(async () => {
    if (!activeAccount) {
      setPosts([]);
      setPage(0);
      return;
    }
    setLoading(true);
    setPosts([]);
    try {
      const list = await api.listScheduledPosts(activeAccount.id);
      list.sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
      setPosts(list);
      setPage(0);
    } finally {
      setLoading(false);
    }
  }, [activeAccount]);

  useEffect(() => {
    load();
  }, [load]);

  const imagesRef = React.useRef(images);
  imagesRef.current = images;

  useEffect(() => {
    return () => {
      imagesRef.current.forEach((img) => {
        if (img.file) URL.revokeObjectURL(img.url);
      });
    };
  }, []);

  const resetForm = useCallback(() => {
    setImages((prev) => {
      prev.forEach((img) => {
        if (img.file) URL.revokeObjectURL(img.url);
      });
      return [];
    });
    setCaption('');
    setHashtags('');
    setScheduledAt(defaultScheduleLocal());
    setSlideIndex(0);
    setDragging(false);
    setError('');
    setEditingPost(null);
  }, []);

  const closeModal = useCallback(() => {
    if (submitting) return;
    suggestReqId.current += 1;
    setModalOpen(false);
    resetForm();
  }, [submitting, resetForm]);

  const openCreate = useCallback(async () => {
    resetForm();
    setModalOpen(true);
    if (!activeAccount) return;
    const reqId = ++suggestReqId.current;
    try {
      const suggested = await api.suggestScheduleTime(activeAccount.id);
      if (reqId !== suggestReqId.current) return;
      const when = new Date(suggested);
      if (!Number.isNaN(when.getTime())) {
        setScheduledAt(toLocalInputValue(when));
      }
    } catch {
      // keep defaultScheduleLocal from resetForm
    }
  }, [resetForm, activeAccount]);

  const openEdit = useCallback((p: api.ScheduledPost) => {
    suggestReqId.current += 1;
    setImages((prev) => {
      prev.forEach((img) => {
        if (img.file) URL.revokeObjectURL(img.url);
      });
      return [];
    });
    const urls = p.imageUrls?.length ? p.imageUrls : p.imageUrl ? [p.imageUrl] : [];
    setEditingPost(p);
    setCaption(p.caption ?? '');
    setHashtags((p.hashtags ?? []).join(' '));
    setScheduledAt(toLocalInputValue(new Date(p.scheduledAt)));
    setImages(urls.map(remoteImageItem));
    setSlideIndex(0);
    setDragging(false);
    setError('');
    setModalOpen(true);
  }, []);

  useEffect(() => {
    if (!modalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [modalOpen, closeModal]);

  useEffect(() => {
    if (slideIndex >= images.length) {
      setSlideIndex(Math.max(0, images.length - 1));
    }
  }, [images.length, slideIndex]);

  function addFiles(fileList: FileList | File[] | null | undefined) {
    if (!fileList) return;
    const incoming = Array.from(fileList).filter((f) => ACCEPTED_IMAGE_TYPES.has(f.type));
    if (incoming.length === 0) {
      setError(t('schedule.manual.errors.imageType'));
      return;
    }
    setError('');
    // New uploads replace remote placeholders so the API can send a full replacement set.
    setImages((prev) => {
      const hadRemote = prev.some((img) => img.remote);
      const base = hadRemote
        ? (() => {
            prev.forEach((img) => {
              if (img.file) URL.revokeObjectURL(img.url);
            });
            return [] as ImageItem[];
          })()
        : prev;
      const room = MAX_IMAGES - base.length;
      if (room <= 0) {
        setError(t('schedule.manual.errors.tooManyImages'));
        return base;
      }
      const next = [...base, ...incoming.slice(0, room).map(newImageItem)];
      if (incoming.length > room) {
        setError(t('schedule.manual.errors.tooManyImages'));
      }
      return next;
    });
    setSlideIndex(0);
  }

  function removeImage(id: string) {
    setImages((prev) => {
      const target = prev.find((img) => img.id === id);
      if (target?.file) URL.revokeObjectURL(target.url);
      return prev.filter((img) => img.id !== id);
    });
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }

  async function cancel(postId: string) {
    if (!activeAccount) return;
    await api.cancelScheduledPost(activeAccount.id, postId);
    load();
  }

  async function publishNow(postId: string) {
    if (!activeAccount || publishingId) return;
    setPublishingId(postId);
    setError('');
    try {
      await api.publishScheduledPostNow(activeAccount.id, postId);
      await load();
    } catch (err) {
      setError(
        err instanceof AppError
          ? err.message
          : t('schedule.publishFailed'),
      );
      await load();
    } finally {
      setPublishingId(null);
    }
  }

  async function submitForm(e: React.FormEvent) {
    e.preventDefault();
    if (!activeAccount) return;
    if (editingPost && !isFutureScheduled(editingPost)) return;
    setError('');
    setSubmitting(true);
    try {
      const when = new Date(scheduledAt);
      if (Number.isNaN(when.getTime())) {
        setError(t('schedule.manual.errors.invalidTime'));
        return;
      }
      if (editingPost) {
        const newFiles = images.map((img) => img.file).filter((f): f is File => !!f);
        const originalUrls = editingPost.imageUrls?.length
          ? editingPost.imageUrls
          : editingPost.imageUrl
            ? [editingPost.imageUrl]
            : [];
        const remoteUrls = images.filter((img) => img.remote).map((img) => img.url);
        const imagesUnchanged =
          newFiles.length === 0 &&
          remoteUrls.length === originalUrls.length &&
          remoteUrls.every((u, i) => u === originalUrls[i]);
        if (images.length === 0) {
          setError(t('schedule.manual.errors.imageRequired'));
          return;
        }
        if (!imagesUnchanged && newFiles.length === 0) {
          setError(t('schedule.manual.errors.imageRequired'));
          return;
        }
        await api.updateScheduledPost(activeAccount.id, editingPost.id, {
          caption: caption.trim(),
          hashtags,
          scheduledAt: when.toISOString(),
          images: imagesUnchanged ? undefined : newFiles,
        });
      } else {
        if (images.length === 0) return;
        await api.createManualScheduledPost(activeAccount.id, {
          caption: caption.trim(),
          hashtags,
          scheduledAt: when.toISOString(),
          images: images.map((img) => img.file!).filter(Boolean),
        });
      }
      setModalOpen(false);
      resetForm();
      load();
    } catch (err) {
      setError(
        err instanceof AppError
          ? err.message
          : t(editingPost ? 'schedule.manual.errors.updateFailed' : 'schedule.manual.errors.submitFailed'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!activeAccount) {
    return <NoAccountState />;
  }

  const username = activeAccount.igUsername || 'account';
  const previewCaption = formatPreviewCaption(caption, hashtags);
  const hashtagTokens = parseHashtagTokens(hashtags);
  const hasCarousel = images.length > 1;
  const currentPreview = images[slideIndex];
  const isEditing = !!editingPost;
  const isReadOnly = !!editingPost && !isFutureScheduled(editingPost);
  const canSubmit =
    !isReadOnly &&
    !!caption.trim() &&
    images.length > 0 &&
    (isEditing || images.every((img) => img.file));
  const modalTitleKey = isReadOnly
    ? 'schedule.manual.viewTitle'
    : isEditing
      ? 'schedule.manual.editTitle'
      : 'schedule.manual.title';
  const modalHintKey = isReadOnly
    ? 'schedule.manual.viewHint'
    : isEditing
      ? 'schedule.manual.editHint'
      : 'schedule.manual.hint';

  const filteredPosts = posts.filter((p) => {
    const inScheduled = SCHEDULED_BUCKET.has(p.status);
    const inPublished = p.status === 'published';
    return (showScheduled && inScheduled) || (showPublished && inPublished);
  });
  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pagePosts = filteredPosts.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  function toggleFilter(kind: 'scheduled' | 'published') {
    if (kind === 'scheduled') {
      if (showScheduled && !showPublished) return; // keep at least one filter on
      setShowScheduled((v) => !v);
    } else {
      if (showPublished && !showScheduled) return;
      setShowPublished((v) => !v);
    }
    setPage(0);
  }

  function goPrev() {
    setSlideIndex((i) => (i <= 0 ? images.length - 1 : i - 1));
  }
  function goNext() {
    setSlideIndex((i) => (i >= images.length - 1 ? 0 : i + 1));
  }

  const modal =
    typeof document !== 'undefined'
      ? createPortal(
          <AnimatePresence>
            {modalOpen && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="presentation">
                <motion.button
                  type="button"
                  aria-label={t('schedule.manual.cancel')}
                  className="absolute inset-0 bg-gray-900/50 dark:bg-black/60 backdrop-blur-[2px]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: reduceMotion ? 0 : 0.15 }}
                  onClick={closeModal}
                />
                <motion.div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby={titleId}
                  aria-describedby={descId}
                  className="relative w-full max-w-4xl max-h-[92vh] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl shadow-gray-900/10 dark:border-lc-border dark:bg-lc-surface dark:shadow-black/40"
                  initial={reduceMotion ? false : { opacity: 0, y: 12, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={reduceMotion ? undefined : { opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: reduceMotion ? 0 : 0.2, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div
                    aria-hidden
                    className="absolute inset-x-0 top-0 z-10 h-1 bg-gradient-to-r from-brand-purple via-brand-pink to-brand-orange"
                  />

                  <div className="flex items-start justify-between gap-3 border-b border-gray-100 dark:border-lc-border px-5 pt-6 pb-4">
                    <div>
                      <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-lc-text">
                        {t(modalTitleKey)}
                      </h2>
                      <p id={descId} className="mt-1 text-xs text-gray-500 dark:text-lc-faint">
                        {t(modalHintKey)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-lc-card dark:hover:text-lc-muted"
                      aria-label={t('schedule.manual.cancel')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <form onSubmit={submitForm} className="overflow-y-auto max-h-[calc(92vh-4.5rem)]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:min-h-[420px]">
                      <div className="bg-[#fafafa] dark:bg-lc-bg p-5 flex flex-col border-b md:border-b-0 md:border-r border-gray-100 dark:border-lc-border">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-lc-faint mb-3">
                          {t('schedule.manual.previewLabel')}
                        </p>
                        <div className="mx-auto w-full max-w-[320px] rounded-xl overflow-hidden border border-gray-200 dark:border-lc-border bg-white dark:bg-lc-surface shadow-sm">
                          <div className="flex items-center gap-2.5 px-3 py-2.5">
                            {activeAccount.profilePictureUrl ? (
                              <img
                                src={activeAccount.profilePictureUrl}
                                alt=""
                                className="h-8 w-8 rounded-full object-cover ring-1 ring-gray-200 dark:ring-lc-border"
                              />
                            ) : (
                              <span className="h-8 w-8 rounded-full bg-gradient-to-br from-brand-purple to-brand-pink" />
                            )}
                            <span className="text-xs font-semibold text-gray-900 dark:text-lc-text truncate">
                              {username}
                            </span>
                            <MoreHorizontal className="ml-auto h-4 w-4 text-gray-400" />
                          </div>

                          <div className="relative aspect-square bg-gray-100 dark:bg-lc-card group">
                            {currentPreview ? (
                              <img
                                key={currentPreview.id}
                                src={currentPreview.url}
                                alt=""
                                className="absolute inset-0 h-full w-full object-cover"
                              />
                            ) : (
                              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-gray-300 dark:text-lc-faint">
                                <ImagePlus className="h-10 w-10" />
                                <span className="text-[11px] px-6 text-center">{t('schedule.manual.previewEmpty')}</span>
                              </div>
                            )}

                            {hasCarousel && (
                              <>
                                <button
                                  type="button"
                                  onClick={goPrev}
                                  className="absolute left-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-white/90 text-gray-800 shadow flex items-center justify-center opacity-90 hover:opacity-100"
                                  aria-label={t('schedule.manual.prevImage')}
                                >
                                  <ChevronLeft className="h-4 w-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={goNext}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-white/90 text-gray-800 shadow flex items-center justify-center opacity-90 hover:opacity-100"
                                  aria-label={t('schedule.manual.nextImage')}
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </button>
                                <div className="absolute bottom-2.5 inset-x-0 flex justify-center gap-1.5">
                                  {images.map((img, i) => (
                                    <button
                                      key={img.id}
                                      type="button"
                                      onClick={() => setSlideIndex(i)}
                                      className={`h-1.5 rounded-full transition-all ${
                                        i === slideIndex ? 'w-3 bg-white' : 'w-1.5 bg-white/50'
                                      }`}
                                      aria-label={`${i + 1}`}
                                    />
                                  ))}
                                </div>
                                <span className="absolute top-2 right-2 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white">
                                  {slideIndex + 1}/{images.length}
                                </span>
                              </>
                            )}
                          </div>

                          <div className="px-3 py-2.5">
                            <div className="flex items-center gap-3.5 text-gray-900 dark:text-lc-text">
                              <Heart className="h-5 w-5" />
                              <MessageCircle className="h-5 w-5" />
                              <Send className="h-5 w-5" />
                              <Bookmark className="ml-auto h-5 w-5" />
                            </div>
                            {hasCarousel && (
                              <div className="mt-2 flex justify-center gap-1">
                                {images.map((img, i) => (
                                  <span
                                    key={img.id}
                                    className={`h-1 w-1 rounded-full ${i === slideIndex ? 'bg-[#0095f6]' : 'bg-gray-300 dark:bg-lc-borderStrong'}`}
                                  />
                                ))}
                              </div>
                            )}
                            <p className="mt-2 text-[11px] leading-relaxed text-gray-800 dark:text-lc-muted whitespace-pre-wrap break-words line-clamp-6 min-h-[2.5rem]">
                              {previewCaption ? (
                                <>
                                  <span className="font-semibold text-gray-900 dark:text-lc-text">{username}</span>{' '}
                                  {previewCaption}
                                </>
                              ) : (
                                <span className="text-gray-400 dark:text-lc-faint">{t('schedule.manual.previewCaptionEmpty')}</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="p-5 space-y-4 flex flex-col">
                        <div>
                          <span className="text-xs font-medium text-gray-600 dark:text-lc-muted">
                            {t('schedule.manual.image')}{' '}
                            <span className="text-gray-400 font-normal">({images.length}/{MAX_IMAGES})</span>
                          </span>
                          {!isReadOnly && (
                            <div
                              onDragEnter={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDragging(true);
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setDragging(true);
                              }}
                              onDragLeave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                                setDragging(false);
                              }}
                              onDrop={onDrop}
                              className={`mt-1.5 relative rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
                                dragging
                                  ? 'border-lc-pink bg-lc-pink/5 dark:bg-lc-pink/10'
                                  : 'border-[#e8ddd0] dark:border-lc-border bg-gray-50/60 dark:bg-lc-bg hover:border-lc-pink/40'
                              }`}
                            >
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                multiple
                                className="absolute inset-0 z-10 cursor-pointer opacity-0"
                                onChange={(e) => {
                                  addFiles(e.target.files);
                                  e.target.value = '';
                                }}
                              />
                              <ImagePlus className={`mx-auto h-7 w-7 ${dragging ? 'text-lc-pink' : 'text-gray-400 dark:text-lc-faint'}`} />
                              <p className="mt-2 text-sm font-medium text-gray-700 dark:text-lc-text">
                                {dragging ? t('schedule.manual.dropHere') : t('schedule.manual.dragDrop')}
                              </p>
                              <p className="mt-1 text-xs text-gray-500 dark:text-lc-faint">
                                {t(isEditing ? 'schedule.manual.imageHintEdit' : 'schedule.manual.imageHint')}
                              </p>
                            </div>
                          )}

                          {images.length > 0 && (
                            <div className={`flex gap-2 overflow-x-auto pb-1 ${isReadOnly ? 'mt-1.5' : 'mt-3'}`}>
                              {images.map((img, i) => (
                                <div
                                  key={img.id}
                                  className={`relative shrink-0 h-16 w-16 rounded-lg overflow-hidden border-2 ${
                                    i === slideIndex ? 'border-lc-pink' : 'border-transparent'
                                  }`}
                                >
                                  <button type="button" className="h-full w-full" onClick={() => setSlideIndex(i)}>
                                    <img src={img.url} alt="" className="h-full w-full object-cover" />
                                  </button>
                                  {!isReadOnly && (
                                    <button
                                      type="button"
                                      onClick={() => removeImage(img.id)}
                                      className="absolute top-0.5 right-0.5 h-5 w-5 rounded-full bg-black/70 text-white flex items-center justify-center"
                                      aria-label={t('schedule.manual.removeImage')}
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <label className="block">
                          <span className="text-xs font-medium text-gray-600 dark:text-lc-muted">{t('schedule.manual.caption')}</span>
                          <textarea
                            className="input mt-1.5 w-full text-sm disabled:cursor-default disabled:opacity-80"
                            rows={3}
                            required
                            autoFocus={!isReadOnly}
                            readOnly={isReadOnly}
                            disabled={isReadOnly}
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            placeholder={t('schedule.manual.captionPlaceholder')}
                          />
                        </label>

                        <label className="block">
                          <span className="text-xs font-medium text-gray-600 dark:text-lc-muted">{t('schedule.manual.hashtags')}</span>
                          <input
                            className="input mt-1.5 w-full text-sm disabled:cursor-default disabled:opacity-80"
                            readOnly={isReadOnly}
                            disabled={isReadOnly}
                            value={hashtags}
                            onChange={(e) => setHashtags(e.target.value)}
                            placeholder={t('schedule.manual.hashtagsPlaceholder')}
                          />
                          {hashtagTokens.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {hashtagTokens.map((tag) => (
                                <span
                                  key={tag}
                                  className="rounded bg-[#f0e6f0] px-2 py-1 text-[11px] font-medium text-lc-purple dark:bg-lc-borderStrong dark:text-lc-purple"
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </label>

                        <label className="block">
                          <span className="text-xs font-medium text-gray-600 dark:text-lc-muted">{t('schedule.manual.scheduledAt')}</span>
                          <input
                            type="datetime-local"
                            className="input mt-1.5 w-full text-sm disabled:cursor-default disabled:opacity-80"
                            required
                            readOnly={isReadOnly}
                            disabled={isReadOnly}
                            value={scheduledAt}
                            onChange={(e) => setScheduledAt(e.target.value)}
                          />
                        </label>

                        {error && <p className="text-sm text-red-600">{error}</p>}

                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-1 mt-auto">
                          <button type="button" className="btn-secondary" onClick={closeModal} disabled={submitting}>
                            {t(isReadOnly ? 'schedule.manual.close' : 'schedule.manual.cancel')}
                          </button>
                          {!isReadOnly && (
                            <button type="submit" className="btn-primary" disabled={submitting || !canSubmit}>
                              {submitting
                                ? t(isEditing ? 'schedule.manual.submittingEdit' : 'schedule.manual.submitting')
                                : t(isEditing ? 'schedule.manual.submitEdit' : 'schedule.manual.submit')}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </form>
                </motion.div>
              </div>
            )}
          </AnimatePresence>,
          document.body,
        )
      : null;

  return (
    <div>
      <PageHeader icon={CalendarClock} title={t('schedule.title')} subtitle={t('schedule.subtitle')} />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2" role="group" aria-label={t('schedule.filters.label')}>
          <button
            type="button"
            onClick={() => toggleFilter('scheduled')}
            aria-pressed={showScheduled}
            className={`px-4 py-2 rounded-md text-xs font-semibold transition-colors ${
              showScheduled
                ? 'bg-lc-pink text-white dark:text-lc-ink border border-lc-pink'
                : 'bg-white dark:bg-lc-bg border border-[#e8ddd0] dark:border-lc-border text-[#241c14] dark:text-lc-text hover:border-lc-pink/40'
            }`}
          >
            {t('schedule.filters.scheduled')}
          </button>
          <button
            type="button"
            onClick={() => toggleFilter('published')}
            aria-pressed={showPublished}
            className={`px-4 py-2 rounded-md text-xs font-semibold transition-colors ${
              showPublished
                ? 'bg-lc-pink text-white dark:text-lc-ink border border-lc-pink'
                : 'bg-white dark:bg-lc-bg border border-[#e8ddd0] dark:border-lc-border text-[#241c14] dark:text-lc-text hover:border-lc-pink/40'
            }`}
          >
            {t('schedule.filters.published')}
          </button>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary">
          <Plus className="h-4 w-4" />
          {t('schedule.manual.open')}
        </button>
      </div>

      {modal}

      {error && !modalOpen && (
        <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {loading ? (
        <p className="text-gray-400 dark:text-lc-faint text-sm">{t('common.loading')}</p>
      ) : posts.length === 0 ? (
        <div className="card border-dashed p-10 text-center">
          <span className="icon-chip mx-auto">
            <CalendarClock className="h-5 w-5" />
          </span>
          <p className="mt-4 text-sm text-gray-400 dark:text-lc-faint">{t('schedule.empty')}</p>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="card border-dashed p-10 text-center">
          <span className="icon-chip mx-auto">
            <CalendarClock className="h-5 w-5" />
          </span>
          <p className="mt-4 text-sm text-gray-400 dark:text-lc-faint">{t('schedule.emptyFiltered')}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {pagePosts.map((p) => {
            const status = STATUS_STYLE[p.status] ?? { labelKey: '', className: 'bg-gray-100 text-gray-600 dark:bg-lc-card dark:text-lc-muted' };
            const isManual = p.source === 'manual';
            const urls = p.imageUrls?.length ? p.imageUrls : p.imageUrl ? [p.imageUrl] : [];
            const hashtags = p.hashtags ?? [];
            const editable = isFutureScheduled(p);
            const canView = p.status === 'published';
            const canCancel = p.status === 'scheduled';
            // Show "publish now" on every card that hasn't been published yet.
            const canPublish = p.status !== 'published';
            const isPublishingCard = p.status === 'publishing';
            const isPublishingThis = publishingId === p.id;
            return (
              <article
                key={p.id}
                className="flex flex-col overflow-hidden rounded-xl border border-[#e8ddd0] bg-[#f5eff5] transition-all duration-200 hover:-translate-y-0.5 hover:border-lc-purple/50 dark:border-lc-borderStrong dark:bg-lc-surface dark:hover:border-lc-purple"
              >
                <div className="flex items-center justify-between gap-2 border-b border-[#e8ddd0] bg-white/60 px-4 py-3 dark:border-lc-borderStrong dark:bg-lc-bg/60">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <span className={`badge ${status.className}`}>
                      {status.labelKey ? t(status.labelKey) : p.status}
                    </span>
                    {isManual && (
                      <span className="badge bg-[#f0e6f0] text-lc-purple dark:bg-lc-borderStrong dark:text-lc-muted">
                        {t('schedule.source.manual')}
                      </span>
                    )}
                    {!isManual && (
                      <span className="badge bg-gray-100 text-gray-600 dark:bg-lc-card dark:text-lc-muted">
                        {t('schedule.source.ai')}
                      </span>
                    )}
                  </div>
                  <time className="shrink-0 text-[10px] font-medium text-gray-500 dark:text-lc-faint">
                    {new Date(p.scheduledAt).toLocaleString(i18n.language, {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </time>
                </div>

                <div className="flex flex-1 flex-col gap-3 p-4">
                  {p.caption ? (
                    <p className="text-[13px] leading-relaxed text-gray-800 dark:text-lc-muted line-clamp-4 whitespace-pre-wrap">
                      {p.caption}
                    </p>
                  ) : (
                    <p className="text-[13px] text-gray-400 dark:text-lc-faint">—</p>
                  )}

                  <CardMediaPreview
                    key={p.id}
                    urls={urls}
                    carouselLabel={t('schedule.manual.carouselBadge', { count: urls.length })}
                    prevLabel={t('schedule.manual.prevImage')}
                    nextLabel={t('schedule.manual.nextImage')}
                  />

                  {hashtags.length > 0 && (
                    <CardHashtags
                      tags={hashtags}
                      moreLabel={(count) => t('schedule.hashtagsMore', { count })}
                      onMore={editable || canView ? () => openEdit(p) : undefined}
                    />
                  )}

                  {p.lastError && (
                    <p className="text-xs text-red-500">{p.lastError}</p>
                  )}
                </div>

                {(editable || canView || canCancel || canPublish) && (
                  <div className="flex items-center justify-end gap-1.5 border-t border-[#e8ddd0] bg-white/60 px-4 py-2.5 dark:border-lc-borderStrong dark:bg-lc-bg/60">
                    {canPublish && (
                      <button
                        type="button"
                        onClick={() => publishNow(p.id)}
                        disabled={isPublishingCard || !!publishingId}
                        className="btn-primary !p-2"
                        title={isPublishingCard || isPublishingThis ? t('schedule.publishingNow') : t('schedule.publishNow')}
                        aria-label={isPublishingCard || isPublishingThis ? t('schedule.publishingNow') : t('schedule.publishNow')}
                      >
                        <Upload className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {editable && (
                      <button
                        type="button"
                        onClick={() => openEdit(p)}
                        disabled={!!publishingId}
                        className="btn-secondary !p-2"
                        title={t('schedule.editTitle')}
                        aria-label={t('schedule.editTitle')}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {canView && (
                      <button
                        type="button"
                        onClick={() => openEdit(p)}
                        className="btn-secondary !p-2"
                        title={t('schedule.viewTitle')}
                        aria-label={t('schedule.viewTitle')}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {canCancel && (
                      <button
                        type="button"
                        onClick={() => cancel(p.id)}
                        disabled={!!publishingId}
                        className="btn-secondary !p-2 hover:!border-red-300 hover:!text-red-600 dark:hover:!border-red-500/40 dark:hover:!text-red-400"
                        title={t('schedule.cancelTitle')}
                        aria-label={t('schedule.cancelTitle')}
                      >
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })}
          </div>

          {filteredPosts.length > PAGE_SIZE && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                className="btn-secondary text-xs px-3 py-1.5"
                disabled={safePage <= 0}
                onClick={() => setPage(safePage - 1)}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                {t('common.prev')}
              </button>
              <span className="text-xs font-medium text-gray-500 dark:text-lc-faint tabular-nums">
                {t('schedule.pageOf', { current: safePage + 1, total: totalPages })}
              </span>
              <button
                type="button"
                className="btn-secondary text-xs px-3 py-1.5"
                disabled={safePage >= totalPages - 1}
                onClick={() => setPage(safePage + 1)}
              >
                {t('common.next')}
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
